/**
 * Renders Animus Quarter's exterior: ground surfacing, kerbs, the gate, and a
 * massing block per plot.
 *
 * This is the grey-box stage on purpose. The discipline the street research
 * recommends is to walk it before any facade detail exists — if the ground
 * plane and the massing alone don't read as a street, no amount of shopfront
 * detail will rescue it, and it is far cheaper to find that out now.
 */
import * as pc from 'playcanvas'
import type { AssetZoneController } from '../assetZones'
import { registerCollider, type AABB } from '../colliders'
import { box, matte, KERB, MASSING, GROUND_FLOOR, PIER } from './districtShared'
import { buildGroundSurfaces } from './groundSurfaces'
import { attachBuildingModel, buildMassing, buildBayReveals, windowEmissiveMask } from './districtBuildings'
import { buildEntrance } from './districtEntrances'
import { buildStreetFurniture } from './districtFurniture'
import { buildCampusLandmarks } from './campusLandmarks'
import { spawnDistrictLife, type DistrictLife } from './districtNpcs'
import {
  PLOTS,
  computeKerbs,
  KERB_HEIGHT, MAX_SURFACE_SEGMENT,
  GATE_Z, GATE_PIER_HALF_SPACING, GATE_PIER_SIZE, GATE_PIER_HEIGHT,
  STEM_MIN_X, STEM_MAX_X,
} from './district'

export interface DistrictHandles {
  root: pc.Entity
  /** Ambient street life (pedestrians + parked cars); frame() drives the NPCs. */
  life: DistrictLife
}

/**
 * Batch only the immediate procedural street geometry. GLB buildings, cars,
 * and skinned pedestrians attach asynchronously after this pass and therefore
 * retain their own culling/animation. A 24 m maximum AABB keeps each batch
 * smaller than a street block instead of turning the quarter into one
 * permanently-visible super-mesh.
 */
function batchImmediateDistrictGeometry(
  app: pc.Application,
  root: pc.Entity,
): void {
  const group = app.batcher?.addGroup('district-static', false, 24)
  if (!group) return
  for (const render of root.findComponents('render') as pc.RenderComponent[]) {
    render.batchGroupId = group.id
  }
}

export function setupDistrict(
  app: pc.Application,
  assetZones?: AssetZoneController<pc.Asset>,
): DistrictHandles {
  const root = new pc.Entity('district')
  app.root.addChild(root)

  const kerbMat = matte(KERB, 0.3)
  const massMat = matte(MASSING, 0.2)
  const groundFloorMat = matte(GROUND_FLOOR, 0.35)
  const pierMat = matte(PIER, 0.25)

  // ── Ground ──────────────────────────────────────────────────────────────
  // Real asphalt / paving / concrete over the street, with road markings and a
  // zebra crossing. Patches are pre-segmented to 16 m by computeSurfaces (the
  // engine frustum-culls on a bounding SPHERE, so an unsegmented 76 m strip
  // would never cull), and the PBR maps load best-effort behind first paint.
  buildGroundSurfaces(app, root)

  // Beyond the street rectangles there was NOTHING — walk to either end of
  // the High Street, or look sideways from the spawn, and the pavement stopped
  // in mid-air over the sky's dark lower hemisphere. One large ground plane
  // under everything gives the district something to stand on.
  const groundMat = matte(new pc.Color(0.30, 0.31, 0.29), 0.15)
  for (let gx = -160; gx < 160; gx += 80) {
    for (let gz = -160; gz < 160; gz += 80) {
      // Segmented for the same reason as everything else: culling is done on
      // a sphere, so one enormous plane would be drawn from everywhere.
      // Kept well below the street surface (y=0) so the textured road planes
      // never z-fight this backdrop terrain — its top face is at -0.10.
      box(root, `terrain-${gx}-${gz}`,
        [gx + 40, -0.15, gz + 40], [80, 0.1, 80], groundMat)
    }
  }

  // Ground collider: one flat slab under the whole district.
  const groundBox: AABB = {
    minX: -60, maxX: 70, minY: -1, maxY: 0,
    minZ: -55, maxZ: 45,
  }
  registerCollider(groundBox)

  // ── Kerbs ───────────────────────────────────────────────────────────────
  // Segmented on the real 915 mm kerb-unit length, both because it is the
  // right module and because it keeps each piece independently cullable.
  for (const run of computeKerbs()) {
    const span = run.maxX - run.minX
    // Segment on the SAME 16 m rule as the ground, not on the 0.915 m kerb
    // unit. The runs are colinear and share one material, so the real module
    // is invisible — and unit-length segmentation cost 151 draw calls for
    // three straight lines. Nine is plenty to stay frustum-cullable.
    const units = Math.max(1, Math.ceil(span / MAX_SURFACE_SEGMENT))
    const step = span / units
    for (let i = 0; i < units; i++) {
      const cx = run.minX + (i + 0.5) * step
      box(root, `kerb-${run.id}-${i}`,
        [cx, KERB_HEIGHT / 2, run.z], [step, KERB_HEIGHT, 0.14], kerbMat)
    }
  }

  // ── Plot massing ────────────────────────────────────────────────────────
  // One emissive window mask, shared by every building's cloned material.
  const winMask = windowEmissiveMask(app)
  for (const plot of PLOTS) {
    attachBuildingModel(app, root, plot, winMask, assetZones)
    buildMassing(root, plot, massMat, groundFloorMat)
    buildEntrance(app, root, plot)
    // Bay piers only where there is no real facade to read.
    if (!plot.model) buildBayReveals(root, plot, pierMat)
  }

  buildStreetFurniture(root)
  buildCampusLandmarks(root, assetZones)

  // ── Gate ────────────────────────────────────────────────────────────────
  // Two piers and a beam: the threshold that says "you have arrived somewhere"
  // and, not incidentally, the frame that composes the view of the anchor.
  for (const sx of [-GATE_PIER_HALF_SPACING, GATE_PIER_HALF_SPACING]) {
    box(root, `gate-pier-${sx}`,
      [sx, GATE_PIER_HEIGHT / 2, GATE_Z],
      [GATE_PIER_SIZE, GATE_PIER_HEIGHT, GATE_PIER_SIZE], pierMat, true)
    registerCollider({
      minX: sx - GATE_PIER_SIZE / 2, maxX: sx + GATE_PIER_SIZE / 2,
      minY: 0, maxY: GATE_PIER_HEIGHT,
      minZ: GATE_Z - GATE_PIER_SIZE / 2, maxZ: GATE_Z + GATE_PIER_SIZE / 2,
    })
  }
  box(root, 'gate-beam',
    [0, GATE_PIER_HEIGHT + 0.35, GATE_Z],
    [GATE_PIER_HALF_SPACING * 2 + GATE_PIER_SIZE, 0.7, GATE_PIER_SIZE * 0.6], pierMat, true)

  // Backdrop blocks closing each end of the High Street. Without them the
  // street's one strong axis runs off into empty ground — a vista has to stop
  // on something or it reads as an unfinished level.
  const backdropMassMat = matte(new pc.Color(0.46, 0.45, 0.47), 0.2)
  // Keep the vista stops beyond the playable perimeter. Pulling the east stop
  // too close turned it into a blank wall beside the authored Datacentre.
  for (const ex of [-50, 58]) {
    box(root, `end-block-${ex}`, [ex, 7, 0], [8, 14, 30], backdropMassMat)
  }

  // ── Perimeter ───────────────────────────────────────────────────────────
  // Keeps the player in the district without a visible wall: the blocks
  // themselves are solid, so only the open ends need closing.
  const WALL_Y = 6
  const ends: AABB[] = [
    // Behind the gate.
    { minX: -60, maxX: 70, minY: 0, maxY: WALL_Y, minZ: GATE_Z + 1, maxZ: GATE_Z + 2 },
    // Both ends of the High Street.
    { minX: -41, maxX: -40, minY: 0, maxY: WALL_Y, minZ: -50, maxZ: 40 },
    { minX: 40, maxX: 41, minY: 0, maxY: WALL_Y, minZ: -50, maxZ: 40 },
    // Sides of the stem, closing the gap between the gate and the south blocks.
    // Run these PAST the gate wall — stopping at GATE_Z left a 1 m slot you
    // could slip through into the backlands behind the south blocks.
    { minX: STEM_MIN_X - 1, maxX: STEM_MIN_X, minY: 0, maxY: WALL_Y, minZ: 9, maxZ: GATE_Z + 2 },
    { minX: STEM_MAX_X, maxX: STEM_MAX_X + 1, minY: 0, maxY: WALL_Y, minZ: 9, maxZ: GATE_Z + 2 },
  ]
  for (const e of ends) registerCollider(e)

  // ── Ambient life ──────────────────────────────────────────────────────────
  // Pedestrians on the footways and a few parked cars, parented to the district
  // root (NOT worldRoot, which is the office island). An empty street reads as
  // a film set; this is what makes it feel inhabited.
  // Batch the synchronous street shell before adding the moving ambient
  // figures. Running this after spawnDistrictLife would overwrite their
  // dynamic batch group with the static district group and freeze transforms.
  batchImmediateDistrictGeometry(app, root)
  const life = spawnDistrictLife(app, root)

  return { root, life }
}
